import styled from 'styled-components/macro';
import 'chartjs-adapter-moment';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useWallets } from '@web3-onboard/react'
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Color } from '../utils/Color';
import tokenMetadataMap from "../static/token-metadata.json";
import { useAssetCategories } from '../hooks/AssetCategories';
import { ethers } from 'ethers';

const ASSET_BREAKDOWN_URL = "https://api.dovish.finance/asset_breakdown"

const AssetAllocation = () => {
    const graphRef = useRef(null);
    const [legendItems, setLegendItems] = useState([]);

    const connectedWallets = useWallets()

    const [walletBalances, setWalletBalances] = useState([]);
    const walletTokens = useMemo(() => {
        return walletBalances.map(e => e.symbol)
    }, [walletBalances])

    useEffect(() => {
        if (connectedWallets.length == 0) return;

        const wallet = connectedWallets[0]; // arbitrarily select first wallet
        const walletAddress = wallet.accounts[0].address;  // arbitrarily select first account
        // const walletAddress = "0x0000000000000000000000000000000000000000";
        const alchemyProvider = new ethers.providers.AlchemyProvider("homestead", "VclcQ9YdPqCaPh179jLmozN0NIN_zQB6")
        
        const getTokenBalances = async () => {
            const addresses = Object.keys(tokenMetadataMap)
            const balancePromises = []
            for (let i = 0; i < addresses.length; i += 100) {
                const currAddresses = addresses.slice(i, Math.min(i + 100, addresses.length))
                balancePromises.push(alchemyProvider.send("alchemy_getTokenBalances", [walletAddress, currAddresses]))
            }

            const balances = (await Promise.all(balancePromises)).flatMap(e => e.tokenBalances);
            const validBalances = balances.filter((balance) => balance.tokenBalance > 0);
            
            setWalletBalances(validBalances.map((balance) => {
                return {
                    ...tokenMetadataMap[balance.contractAddress],
                    balance: balance.tokenBalance
                };
            }));
        }

        getTokenBalances();
    }, [connectedWallets])

    console.log(legendItems)

    const legendBar = legendItems.map((l) => {
        return (<LegendElement key={l.text}>
            <LegendColor legendColor={l.fillStyle}/>
            <LegendText>{l.text}</LegendText>
        </LegendElement>)
    })

    ChartJS.register(ArcElement, Tooltip, Legend);
    const {categoryMap, categoryList} = useAssetCategories(walletTokens);

    useEffect(() => {
        if (graphRef.current !== null) {
            setLegendItems(graphRef.current.legend.legendItems)
        }
    }, [graphRef, categoryMap])


    const data = {
        labels: categoryMap.map(d => d.category),
        datasets: [{
            label: 'Sector Exposure',
            data: categoryMap.map(d => d.count),
            backgroundColor: chartColors
        }]
    }

    const chartOptions = {
        maintainAspectRatio: false,
        cutout: 120,
        borderWidth: 0,
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                fullWidth: true
            }
        }
    }

    const walletListingData = walletBalances.map((e, i) => {
        return {
            tokenName: e.name,
            amount: e.balance / (1 ** e.decimals),
            logo: e.logo,
            category: (categoryList !== null) ? categoryList[i] : ""
        }
    })

    const totalCategoryCount = categoryMap.reduce((accumulator, currentCount) => {
        return accumulator + currentCount.count;
      }, 0);

    
    const categoryPercentages = categoryMap.map(category => ({
        category: category.category,
        percentage: (category.count / totalCategoryCount) * 100,
      }));

    const CategoryList = ({ categoryPercentages }) => {
        return (
            <ListWrapper>
            {categoryPercentages.map((category) => (
                <ListItem key={category.category}>
                <CategoryName>{category.category}</CategoryName>
                <CategoryPercentage>{Math.round(category.percentage)}%</CategoryPercentage>
                </ListItem>
            ))}
            </ListWrapper>
        );
    };


    return (
        <SectorExposureWrapper>
            <Headline>My Positions</Headline>
            <SectorBreakdown>
            <GraphWrapper>
                <Doughnut data={data} options={chartOptions} ref={graphRef} />
                <Title>Sector<br />Exposure<Hint>Hover to view token category</Hint></Title>
            </GraphWrapper>
            <CategoryList categoryPercentages={categoryPercentages} />
            {/* <GraphLegendWrapper>
                <GraphLegend>
                    {legendBar}
                </GraphLegend>
                
            </GraphLegendWrapper> */}
            </SectorBreakdown>
            {/* <WalletListing data={walletListingData}/>
            {!isConnected && <button onClick={() => connect()}>Connect</button>} */}
        </SectorExposureWrapper>
    );
};

const SectorExposureWrapper = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    background: radial-gradient(123.24% 202.49% at -10.54% -15.29%, rgba(238, 237, 253, 0.7) 0%, rgba(255, 255, 255, 0) 100%) /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */;    border-radius: 10px;
    /* box-shadow: 5px 5px 8px rgba(0,0,0,0.2); */
    overflow: auto;
    border: 1px solid rgba(0,0,0,0.02);

    @media screen and (max-width: 768px) {
        width: auto;
        height: fit-content;
        overflow: hidden;
    }
`;

const Headline = styled.h1`
font-size: 1.2em;
margin: 0;
text-align: left;
margin-left: 2rem;
margin-top: 2rem;
font-weight: 800;
@media screen and (max-width: 768px) {
    margin-top: 0.5rem;
}
`;

const Hint = styled.p`
margin-top: 0.5rem;
font-weight: bold;
color: rgba(0,0,0,0.7);
font-size: 0.65em;
`

// const GraphWrapper = styled.div`
//     display: flex;
//     align-content: center;
//     align-items: center;
//     justify-content: center;
//     justify-items: center;
//     position: relative;
//     height: 20rem;
//     width: 80%;
//     padding-left: 10%;
//     padding-right: 10%;
//     margin-top: 1rem;
//     margin-bottom: 1rem;
//     @media screen and (max-width: 768px) {
//         height: 18rem;
//         min-height: fit-content;
//     }
// `;

const GraphWrapper = styled.div`
    flex: 1;
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
    justify-items: center;
    position: relative;
    height: 20rem;
    width: 100%;
    padding-left: 10%;
    padding-right: 10%;
    margin-top: 1rem;
    margin-bottom: 1rem;
    @media screen and (max-width: 768px) {
        height: 18rem;
        min-height: fit-content;
    }
`;

const LegendText = styled.p`
display: inline-block;
margin: 0;
font-size: 0.6em;
`

const LegendElement = styled.div`
display: inline-block;
margin-right: 1rem;
height: fit-content;
text-align: left;
width: fit-content;
white-space: nowrap;
align-items: center;
`

const LegendColor = styled.div`
display: inline-block;
background-color: ${props => props.legendColor};
width: 10px;
height: 10px;
margin-right: 5px;
`

const GraphLegendWrapper = styled.div`
max-width: 100%;
padding: 0 1rem;
`

const GraphLegend = styled.span`
position: relative;
display: inline-block;
overflow-x: auto;
overflow-y: hidden;
white-space: nowrap;
width:0;
min-width:100%;
min-height: 35px;
`



const SectorBreakdown = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 1000px;
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ListWrapper = styled.ul`
  flex: 1;
  max-width: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding-left: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  max-width: 100%;
`;

const CategoryName = styled.span`
  float: left;
`;

const CategoryPercentage = styled.span`
  float: right;
`;




const Title = styled.h1`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -65%);
    width: 100%;
    font-size: 1.3em;
    font-weight: 800;
    text-align: center;
`;

const chartColors = [
    '#3ec2e7',
    '#2ee7ba',
    '#ed825c',
    '#f5e0a8',
    '#9e75f0',
    '#ec9f53',
    '#08CF9F',
    '#C6E1F6',
    '#000000',
];

const topTokens = [{"name":"TrueUSD","symbol":"TUSD","address":"0x0000000000085d4780B73119b644AE5ecd22b376","decimals":18},{"name":"KuCoin","symbol":"KCS","address":"0x039B5649A59967e3e936D7471f9c3700100Ee1ab","decimals":6},{"name":"Gemini dollar","symbol":"GUSD","address":"0x056Fd409E1d7A124BD7017459dFEa2F387b6d5Cd","decimals":2},{"name":"Zilliqa","symbol":"ZIL","address":"0x05f4a42e251f2d52b8ed15E9FEdAacFcEF1FAD27","decimals":12},{"name":"Bitether","symbol":"BTT","address":"0x080aa07E2C7185150d7e4DA98838A8d2feac3dfC","decimals":0},{"name":"BlockEstate Investment Token","symbol":"BIT","address":"0x089B85FA15f72c1088CBbef23a49DB80B91DD521","decimals":8},{"name":"Render Token","symbol":"RNDR","address":"0x0996bFb5D057faa237640E2506BE7B4f9C46de0B","decimals":18},{"name":"yearn.finance","symbol":"YFI","address":"0x0bc529c00C6401aEF6D220BE8C6Ea1667F6Ad93e","decimals":18},{"name":"Basic Attention Token","symbol":"BAT","address":"0x0D8775F648430679A709E98d2b0Cb6250d2887EF","decimals":18},{"name":"Synapse","symbol":"SYN","address":"0x10B123FdDde003243199aaD03522065dC05827A0","decimals":18},{"name":"Uniswap","symbol":"UNI","address":"0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984","decimals":18},{"name":"Wrapped Bitcoin","symbol":"WBTC","address":"0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599","decimals":8},{"name":"HEX","symbol":"HEX","address":"0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39","decimals":8},{"name":"Reserve Rights","symbol":"RSR","address":"0x320623b8E4fF03373931769A31Fc52A4E78B5d70","decimals":18},{"name":"Theta Token","symbol":"THETA","address":"0x3883f5e181fccaF8410FA61e12b59BAd963fb645","decimals":18},{"name":"WAX","symbol":"WAX","address":"0x39Bb259F66E1C59d5ABEF88375979b4D20D98022","decimals":8},{"name":"XinFin Network","symbol":"XDCE","address":"0x41AB1b6fcbB2fA9DCEd81aCbdeC13Ea6315F2Bf2","decimals":18},{"name":"Paxos Gold","symbol":"PAXG","address":"0x45804880De22913dAFE09f4980848ECE6EcbAf78","decimals":18},{"name":"Quant","symbol":"QNT","address":"0x4a220E6096B25EADb88358cb44068A3248254675","decimals":18},{"name":"Menlo One","symbol":"ONE","address":"0x4D807509aECe24C0fa5A102b6a3B059Ec6E14392","decimals":18},{"name":"Fantom Token","symbol":"FTM","address":"0x4E15361FD6b4BB609Fa63C81A2be19d873717870","decimals":18},{"name":"CelerToken","symbol":"CELR","address":"0x4F9254C83EB525f9FCf346490bbb3ed28a81C667","decimals":18},{"name":"Binance USD (BUSD)","symbol":"BUSD","address":"0x4Fabb145d64652a948d72533023f6E7A623C7C53","decimals":18},{"name":"Livepeer Token","symbol":"LPT","address":"0x58b6A8A3302369DAEc383334672404Ee733aB239","decimals":18},{"name":"Gnosis","symbol":"GNO","address":"0x6810e776880C02933D47DB1b9fc05908e5386b96","decimals":18},{"name":"Dai Stablecoin v2.0","symbol":"DAI","address":"0x6B175474E89094C44Da98b954EedeAC495271d0F","decimals":18},{"name":"SushiToken","symbol":"SUSHI","address":"0x6B3595068778DD592e39A122f4f5a5cF09C90fE2","decimals":18},{"name":"Huobi Token","symbol":"HT","address":"0x6f259637dcD74C767781E37Bc6133cd6A68aa161","decimals":18},{"name":"IoTeX Network","symbol":"IOTX","address":"0x6fB3e0A217407EFFf7Ca062D46c26E5d60a14d69","decimals":18},{"name":"OKB","symbol":"OKB","address":"0x75231F58b43240C9718Dd58B4967c5114342a86c","decimals":18},{"name":"Matic Token","symbol":"MATIC","address":"0x7D1AfA7B718fb893dB30A3aBc0Cfc608AaCfeBB0","decimals":18},{"name":"SHIBA INU","symbol":"SHIB","address":"0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE","decimals":18},{"name":"HollaEx","symbol":"HEX","address":"0x96006F60B452526481a26eab55265ECdf82E7361","decimals":18},{"name":"Polymath Network","symbol":"POLY","address":"0x9992eC3cF6A55b00978cdDF2b27BC6882d88D1eC","decimals":18},{"name":"MakerDAO","symbol":"MKR","address":"0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2","decimals":18},{"name":"USD//Coin","symbol":"USDC","address":"0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48","decimals":6},{"name":"CEEK VR Token","symbol":"CEEK","address":"0xb056c38f6b7Dc4064367403E26424CD2c60655e1","decimals":18},{"name":"Rocket Pool","symbol":"RPL","address":"0xB4EFd85c19999D84251304bDA99E90B92300Bd93","decimals":18},{"name":"Nexo","symbol":"NEXO","address":"0xB62132e35a6c13ee1EE0f84dC5d40bad8d815206","decimals":18},{"name":"Crypto.com","symbol":"MCO","address":"0xB63B606Ac810a52cCa15e44bB630fd42D8d1d83d","decimals":8},{"name":"Binance Coin","symbol":"BNB","address":"0xB8c77482e45F1F44dE1745F52C74426C631bDD52","decimals":18},{"name":"Balancer","symbol":"BAL","address":"0xba100000625a3754423978a60c9317c58a424e3D","decimals":18},{"name":"SwissBorg","symbol":"CHSB","address":"0xba9d4199faB4f26eFE3551D490E3821486f135Ba","decimals":8},{"name":"Loopring","symbol":"LRC","address":"0xBBbbCA6A901c926F240b89EacB641d8Aec7AEafD","decimals":18},{"name":"Compound","symbol":"COMP","address":"0xc00e94Cb662C3520282E6f5717214004A7f26888","decimals":18},{"name":"Synthetix Network Token","symbol":"SNX","address":"0xC011A72400E58ecD99Ee497CF89E3775d4bd732F","decimals":18},{"name":"Synthetix Network Token","symbol":"SNX","address":"0xC011a73ee8576Fb46F5E1c5751cA3B9Fe0af2a6F","decimals":18},{"name":"Graph Token","symbol":"GRT","address":"0xc944E90C64B2c07662A292be6244BDf05Cda44a7","decimals":18},{"name":"Smooth Love Potion","symbol":"SLP","address":"0xCC8Fa225D80b9c7D42F96e9570156c65D6cAAa25","decimals":0},{"name":"OmiseGO","symbol":"OMG","address":"0xd26114cd6EE289AccF82350c8d8487fedB8A0C07","decimals":18},{"name":"VeChain","symbol":"VEN","address":"0xD850942eF8811f2A866692A623011bDE52a462C1","decimals":18},{"name":"USD Tether (erc20)","symbol":"USDT","address":"0xdAC17F958D2ee523a2206206994597C13D831ec7","decimals":6},{"name":"Kyber Network","symbol":"KNC","address":"0xdd974D5C2e2928deA5F71b9825b8b646686BD200","decimals":18},{"name":"0x Project","symbol":"ZRX","address":"0xE41d2489571d322189246DaFA5ebDe1F4699F498","decimals":18},{"name":"ENJIN","symbol":"ENJ","address":"0xF629cBd94d3791C9250152BD8dfBDF380E2a3B9c","decimals":18},{"name":"LEOcoin","symbol":"LEO","address":"0xf97b5d65Da6b0468b90D531ddae2a69843e6797d","decimals":18},{"name":"IOSToken","symbol":"IOST","address":"0xFA1a856Cfa3409CFa145Fa4e20Eb270dF3EB21ab","decimals":18},{"name":"Blocktrade.com","symbol":"BTT","address":"0xFA456Cf55250A839088b27EE32A424d7DAcB54Ff","decimals":18},{"name":"Amp","symbol":"AMP","address":"0xfF20817765cB7f73d4bde2e66e067E58D11095C2","decimals":18},{"name":"Orbs","symbol":"ORBS","address":"0xff56Cc6b1E6dEd347aA0B7676C85AB0B3D08B0FA","decimals":18}];


export default AssetAllocation;